// demo-dashboard.js — lesson picker (Brief 003).
//
// Lesson boxes are derived from `content/demo-mmus.json` (via the content
// provider) — never a hardcoded lesson count (Brief 003 §6). Currently 4
// training lessons + Testing, but the grid renders whatever the content ships.
// Training boxes show title + word count and route to `/exercise/:itemId`;
// the Testing box routes to `/test/:itemId`. Layout mirrors the Unity dashboard
// (unskinned — the dojo skin is Brief 006).

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import * as lang from "../i18n/language-manager.js";
import { loadContent, getExercises } from "../data/demo-content-provider.js";

export class L2BDemoDashboard extends KFEComponent {
  static styles = `
    :host { display: block; }
    /* Section heading on the dojo NamePlate scroll (sprite border-image with the
       cream centre; dark ink text). Falls back to plain centred text when no skin
       nameplate token is set. */
    h2 {
      font-size: var(--l2b-h2); margin: 0 auto 1.5rem; text-align: center;
      max-width: 24rem; padding: 0.7rem 2.4rem; color: #4a2f1a; font-weight: 800;
      border: 22px solid transparent; border-width: 18px 46px;
      border-image: var(--l2b-skin-nameplate) 44 76 fill / 18px 46px stretch;
    }
    .grid {
      display: grid; gap: 1.1rem;
      grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
      max-width: 44rem; margin: 0 auto;
    }
    /* Lesson tiles framed in the dojo button sprite (BTN.webp, cream centre →
       dark ink). Square tiles preserved via aspect-ratio: 1/1. */
    a.box {
      display: flex; flex-direction: column; align-items: center;
      justify-content: center; gap: 0.35rem;
      aspect-ratio: 1 / 1; text-decoration: none; text-align: center;
      padding: 1rem; color: #4a2f1a;
      border: var(--l2b-btn-border) solid transparent;
      border-image: var(--l2b-skin-btn) var(--l2b-btn-slice) / var(--l2b-btn-border) stretch;
      filter: drop-shadow(0 3px 6px rgba(0,0,0,0.45));
      transition: transform 0.12s ease, filter 0.12s ease;
    }
    a.box:hover { transform: translateY(-3px) scale(1.02);
                  filter: drop-shadow(0 6px 12px rgba(0,0,0,0.5)) brightness(1.04); }
    a.box:active { transform: translateY(0) scale(0.99); }
    .box .title { font-weight: 800; }
    .box .meta { font-size: 0.8rem; color: #7a5230; }
    /* Testing tile gets the dojo-red star icon + tinted meta to set it apart. */
    a.box.testing .title::before {
      content: ""; display: inline-block; width: 1em; height: 1em;
      margin-right: 0.35em; vertical-align: -0.12em;
      background: var(--l2b-skin-star) center / contain no-repeat;
    }
    .box.testing .meta { color: var(--l2b-accent); font-weight: 700; }

    /* ── Dojo stage: the animated entrance door + the genie mascot, dressing the
       top of the dashboard as the dojo room. Decorative only — no engine state. ── */
    .dojo-stage {
      position: relative; max-width: 44rem; margin: 0 auto 1.5rem;
      display: flex; align-items: flex-end; justify-content: center; gap: clamp(0.5rem, 3vw, 2rem);
    }
    /* Door open: four webp frames (0 closed → 3 open) stacked and cross-revealed
       by staggered opacity keyframes — a CSS sprite-swap of the source Door_000x
       sequence. Plays once on mount, then holds open. */
    .door {
      position: relative; width: clamp(110px, 22vw, 168px);
      aspect-ratio: 430 / 660; flex: none;
      filter: drop-shadow(0 8px 16px rgba(0,0,0,0.55));
    }
    .door .frame {
      position: absolute; inset: 0; opacity: 0;
      background-size: contain; background-position: center bottom; background-repeat: no-repeat;
    }
    .door .f0 { background-image: url("https://cdn.speechgenie.online/l2b/skin/door/0.webp"); }
    .door .f1 { background-image: url("https://cdn.speechgenie.online/l2b/skin/door/1.webp"); }
    .door .f2 { background-image: url("https://cdn.speechgenie.online/l2b/skin/door/2.webp"); }
    .door .f3 { background-image: url("https://cdn.speechgenie.online/l2b/skin/door/3.webp"); }
    .door .frame { animation-duration: 1.6s; animation-delay: 0.5s;
                   animation-timing-function: steps(1, end);
                   animation-iteration-count: 1; animation-fill-mode: both; }
    .door .f0 { animation-name: doorF0; }
    .door .f1 { animation-name: doorF1; }
    .door .f2 { animation-name: doorF2; }
    .door .f3 { animation-name: doorF3; }
    @keyframes doorF0 { 0%,24%{opacity:1} 25%,100%{opacity:0} }
    @keyframes doorF1 { 0%,24%{opacity:0} 25%,49%{opacity:1} 50%,100%{opacity:0} }
    @keyframes doorF2 { 0%,49%{opacity:0} 50%,74%{opacity:1} 75%,100%{opacity:0} }
    @keyframes doorF3 { 0%,74%{opacity:0} 75%,100%{opacity:1} }

    /* Genie mascot: the muted clip, white field feathered out with a radial mask
       so the gold lamp + rising smoke float over the backdrop with a warm halo. */
    .genie {
      width: clamp(150px, 40vw, 250px); height: auto; flex: none;
      -webkit-mask-image: radial-gradient(ellipse 62% 70% at 50% 46%, #000 52%, transparent 76%);
      mask-image: radial-gradient(ellipse 62% 70% at 50% 46%, #000 52%, transparent 76%);
      filter: drop-shadow(0 10px 20px rgba(0,0,0,0.4));
      animation: l2b-genie-float 4.5s ease-in-out infinite;
    }
    @keyframes l2b-genie-float {
      0%,100% { transform: translateY(0); }
      50%     { transform: translateY(-10px); }
    }
    @media (max-width: 600px) { .genie { width: clamp(130px, 44vw, 200px); } }
    @media (prefers-reduced-motion: reduce) {
      .door .frame { animation: none; } .door .f3 { opacity: 1; }
      .genie { animation: none; }
    }
  `;

  constructor() {
    super();
    /** @type {import("../data/demo-content-provider.js").Exercise[] | null} */
    this._exercises = null;
    /** @type {(() => void) | null} */ this._langOff = null;
  }

  async onConnected() {
    await loadContent();
    // getExercises returns the raw exercises (with mmuIds) — enough for counts.
    this._exercises = /** @type {any} */ (getExercises());
    // Re-render labels if the L1 changes (Gotcha 4 — subscribe via addCleanup).
    if (!this._langOff) {
      this._langOff = lang.onLanguageChanged(() => this._scheduleRender());
      this.addCleanup(() => { this._langOff?.(); this._langOff = null; });
    }
    this._scheduleRender();
  }

  render() {
    if (!this._exercises) return html`<p>${t("loading")}</p>`;

    const boxes = this._exercises.map((ex) => {
      const count = /** @type {any} */ (ex).mmuIds?.length ?? 0;
      const testing = ex.mode === "testing";
      const href = testing ? `/test/${ex.itemId}` : `/exercise/${ex.itemId}`;
      const meta = testing
        ? t("itemType.l2bTesting")
        : count === 1 ? t("dashboard.word", { n: count }) : t("dashboard.words", { n: count });
      return html`
        <a class="box ${testing ? "testing" : ""}" href="${href}">
          <span class="title">${ex.title}</span>
          <span class="meta">${meta}</span>
        </a>
      `;
    });

    return html`
      <section>
        ${this._renderStage()}
        <h2>${t("dashboard.lessons")}</h2>
        <div class="grid">${boxes}</div>
      </section>
    `;
  }

  /**
   * Decorative dojo stage — animated entrance door + genie mascot. Built in the
   * DOM (not the `html` template) so the genie <video> gets its autoplay/inline
   * attributes set as real properties. Purely presentational; touches no engine
   * or content state (brief Gotcha 1).
   * @returns {HTMLElement}
   */
  _renderStage() {
    const stage = document.createElement("div");
    stage.className = "dojo-stage";
    stage.setAttribute("aria-hidden", "true"); // decoration — hidden from AT

    const door = document.createElement("div");
    door.className = "door";
    for (let i = 0; i < 4; i++) {
      const f = document.createElement("div");
      f.className = `frame f${i}`;
      door.appendChild(f);
    }

    // Genie clip — muted + playsinline so iOS autoplays it (brief Gotcha 5).
    const genie = document.createElement("video");
    genie.className = "genie";
    genie.muted = true;
    genie.defaultMuted = true;
    genie.loop = true;
    genie.autoplay = true;
    genie.setAttribute("muted", "");
    genie.setAttribute("playsinline", "");
    genie.setAttribute("webkit-playsinline", "");
    genie.setAttribute("preload", "metadata");
    genie.setAttribute("disablepictureinpicture", "");
    const base = "https://cdn.speechgenie.online/l2b/skin/genie/genie";
    for (const [src, type] of [[`${base}.webm`, "video/webm"], [`${base}.mp4`, "video/mp4"]]) {
      const s = document.createElement("source");
      s.src = src; s.type = type;
      genie.appendChild(s);
    }
    // Belt-and-braces: some engines need an explicit play() even when muted.
    genie.addEventListener("canplay", () => { genie.play().catch(() => {}); }, { once: true });

    stage.append(door, genie);
    return stage;
  }
}
